import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db, functions } from '@/config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  PrimaryLedgerCompany,
  PrimaryLedgerSyncStatus,
  ValidationResult,
  SalesProfitHistory,
  PPCResults,
  MarginHistory
} from '@/types/primaryLedger';

/**
 * Sanitizes a company name for use in Firestore paths.
 * Must match the backend sanitization logic.
 */
function sanitizeCompanyName(name: string): string {
  return name
    .replace(/\s+/g, '_')  // Replace spaces with underscores
    .replace(/[^a-zA-Z0-9_-]/g, '')  // Remove special chars
    .toLowerCase();
}

class PrimaryLedgerService {
  async getAllCompanies(): Promise<PrimaryLedgerCompany[]> {
    try {
      const companiesRef = collection(db, 'primary_ledger_companies');
      const q = query(companiesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PrimaryLedgerCompany));
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  }

  subscribeToCompanies(
    callback: (companies: PrimaryLedgerCompany[]) => void
  ): Unsubscribe {
    const companiesRef = collection(db, 'primary_ledger_companies');
    const q = query(companiesRef, orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
      const companies = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PrimaryLedgerCompany));
      callback(companies);
    }, (error) => {
      console.error('Error in companies subscription:', error);
    });
  }

  async getCompany(companyId: string): Promise<PrimaryLedgerCompany | null> {
    try {
      const docRef = doc(db, 'primary_ledger_companies', companyId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as PrimaryLedgerCompany;
      }
      return null;
    } catch (error) {
      console.error('Error fetching company:', error);
      throw error;
    }
  }

  async validateSpreadsheets(
    salesProfitSpreadsheetId: string,
    ppcResultsSpreadsheetId: string,
    marginHistorySpreadsheetId: string
  ): Promise<ValidationResult> {
    try {
      const validateFunction = httpsCallable(
        functions,
        'validatePrimaryLedgerSpreadsheetAccess'
      );
      
      const result = await validateFunction({
        salesProfitSpreadsheetId,
        ppcResultsSpreadsheetId,
        marginHistorySpreadsheetId
      });
      
      return result.data as ValidationResult;
    } catch (error) {
      console.error('Error validating spreadsheets:', error);
      throw error;
    }
  }

  async createCompany(
    companyName: string,
    salesProfitSpreadsheetId: string,
    ppcResultsSpreadsheetId: string,
    marginHistorySpreadsheetId: string,
    userId: string
  ): Promise<string> {
    try {
      const companiesRef = collection(db, 'primary_ledger_companies');
      const docRef = doc(companiesRef);
      
      const companyData: Omit<PrimaryLedgerCompany, 'id'> = {
        companyName,
        salesProfitSpreadsheetId,
        ppcResultsSpreadsheetId,
        marginHistorySpreadsheetId,
        salesProfitSheetName: '1. ASIN Sales History',
        ppcResultsSheetName: '4. ASIN Results Ledger',
        marginHistorySheetName: 'Margin Ledger',
        syncEnabled: true,
        syncSchedule: 'daily_2am',
        lastSyncDate: null,
        accessValidated: false,
        validationDate: null,
        validationErrors: [],
        createdAt: Timestamp.now(),
        createdBy: userId,
        updatedAt: Timestamp.now(),
        updatedBy: userId
      };
      
      await setDoc(docRef, companyData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }

  async updateCompany(
    companyId: string,
    updates: Partial<Omit<PrimaryLedgerCompany, 'id' | 'createdAt' | 'createdBy'>>
  ): Promise<void> {
    try {
      const docRef = doc(db, 'primary_ledger_companies', companyId);
      await updateDoc(docRef, updates);
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  }

  async deleteCompany(companyId: string): Promise<void> {
    try {
      const docRef = doc(db, 'primary_ledger_companies', companyId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }

  async toggleSyncEnabled(
    companyId: string,
    enabled: boolean
  ): Promise<void> {
    try {
      const docRef = doc(db, 'primary_ledger_companies', companyId);
      await updateDoc(docRef, { syncEnabled: enabled });
    } catch (error) {
      console.error('Error toggling sync:', error);
      throw error;
    }
  }

  async triggerManualSync(companyId: string): Promise<void> {
    try {
      const syncFunction = httpsCallable(
        functions,
        'triggerManualPrimaryLedgerSync'
      );
      
      // Call function (returns immediately)
      const result = await syncFunction({ companyId });
      const response = result.data as { success: boolean; message: string };
      
      if (!response.success) {
        throw new Error(response.message);
      }
      
      // Poll sync status until complete
      return new Promise((resolve, reject) => {
        const statusRef = doc(db, 'primary_ledger_sync_status', companyId);
        
        const checkInterval = setInterval(async () => {
          try {
            const statusDoc = await getDoc(statusRef);
            const status = statusDoc.data();
            
            if (!status?.inProgress) {
              clearInterval(checkInterval);
              if (status?.lastSyncSuccess) {
                resolve();
              } else {
                reject(new Error(status?.error || 'Sync failed'));
              }
            }
          } catch (error) {
            clearInterval(checkInterval);
            reject(error);
          }
        }, 3000); // Poll every 3 seconds
        
        // Timeout after 60 minutes (for initial imports with large datasets)
        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('Sync timeout - operation took longer than 60 minutes'));
        }, 3600000);
      });
    } catch (error) {
      console.error('Error triggering manual sync:', error);
      throw error;
    }
  }

  async triggerManualSyncNonBlocking(companyId: string): Promise<void> {
    try {
      const syncFunction = httpsCallable(
        functions,
        'triggerManualPrimaryLedgerSync'
      );
      
      const result = await syncFunction({ companyId });
      const response = result.data as { success: boolean; message: string };
      
      if (!response.success) {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error triggering manual sync:', error);
      throw error;
    }
  }

  async getSyncStatus(companyId: string): Promise<PrimaryLedgerSyncStatus | null> {
    try {
      const statusRef = doc(db, 'primary_ledger_sync_status', companyId);
      const statusSnap = await getDoc(statusRef);
      
      if (statusSnap.exists()) {
        return statusSnap.data() as PrimaryLedgerSyncStatus;
      }
      return null;
    } catch (error) {
      console.error('Error fetching sync status:', error);
      return null;
    }
  }

  subscribeSyncStatus(
    companyId: string,
    callback: (status: PrimaryLedgerSyncStatus | null) => void
  ): Unsubscribe {
    const statusRef = doc(db, 'primary_ledger_sync_status', companyId);
    
    return onSnapshot(statusRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as PrimaryLedgerSyncStatus);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error in sync status subscription:', error);
      callback(null);
    });
  }

  async getCompanyAsins(companyId: string, companyName: string): Promise<string[]> {
    try {
      const asins = new Set<string>();
      const sanitizedName = sanitizeCompanyName(companyName);
      
      const collectionPaths = [
        `primary_ledger/${sanitizedName}/sales_profit_history`,
        `primary_ledger/${sanitizedName}/ppc_results`,
        `primary_ledger/${sanitizedName}/margin_history`
      ];
      
      for (const path of collectionPaths) {
        const collectionRef = collection(db, path);
        const snapshot = await getDocs(collectionRef);
        
        snapshot.docs.forEach(doc => {
          const asin = doc.data().asin;
          if (asin) {
            asins.add(asin);
          }
        });
      }
      
      return Array.from(asins).sort();
    } catch (error) {
      console.error('Error fetching company ASINs:', error);
      throw error;
    }
  }

  async findCompanyByASIN(asin: string): Promise<string | null> {
    try {
      const sanitizedAsin = asin.trim().toUpperCase();
      
      // Get all companies first
      const companies = await this.getAllCompanies();
      
      // Search through each company's data
      for (const company of companies) {
        const sanitizedName = sanitizeCompanyName(company.companyName);
        const collectionPaths = [
          `primary_ledger/${sanitizedName}/sales_profit_history`,
          `primary_ledger/${sanitizedName}/ppc_results`,
          `primary_ledger/${sanitizedName}/margin_history`
        ];
        
        for (const path of collectionPaths) {
          const collectionRef = collection(db, path);
          const q = query(
            collectionRef,
            where('asin', '==', sanitizedAsin)
          );
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            return company.id;
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding company by ASIN:', error);
      throw error;
    }
  }

  async getSalesProfitHistory(
    companyId: string,
    companyName: string,
    asin: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<SalesProfitHistory[]> {
    try {
      const sanitizedName = sanitizeCompanyName(companyName);
      const collectionRef = collection(db, `primary_ledger/${sanitizedName}/sales_profit_history`);
      let q = query(
        collectionRef,
        where('asin', '==', asin)
      );
      
      if (startDate) {
        q = query(q, where('date', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where('date', '<=', Timestamp.fromDate(endDate)));
      }
      
      q = query(q, orderBy('date', 'asc'));
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SalesProfitHistory));
    } catch (error) {
      console.error('Error fetching sales profit history:', error);
      throw error;
    }
  }

  async getPPCResults(
    companyId: string,
    companyName: string,
    asin: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<PPCResults[]> {
    try {
      const sanitizedName = sanitizeCompanyName(companyName);
      const collectionRef = collection(db, `primary_ledger/${sanitizedName}/ppc_results`);
      let q = query(
        collectionRef,
        where('asin', '==', asin)
      );
      
      if (startDate) {
        q = query(q, where('date', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where('date', '<=', Timestamp.fromDate(endDate)));
      }
      
      q = query(q, orderBy('date', 'asc'));
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PPCResults));
    } catch (error) {
      console.error('Error fetching PPC results:', error);
      throw error;
    }
  }

  async getMarginHistory(
    companyId: string,
    companyName: string,
    asin: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<MarginHistory[]> {
    try {
      const sanitizedName = sanitizeCompanyName(companyName);
      const collectionRef = collection(db, `primary_ledger/${sanitizedName}/margin_history`);
      let q = query(
        collectionRef,
        where('asin', '==', asin)
      );
      
      if (startDate) {
        q = query(q, where('date', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where('date', '<=', Timestamp.fromDate(endDate)));
      }
      
      q = query(q, orderBy('date', 'asc'));
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as MarginHistory));
    } catch (error) {
      console.error('Error fetching margin history:', error);
      throw error;
    }
  }

  subscribeToSalesProfitHistory(
    companyId: string,
    companyName: string,
    asin: string,
    startDate: Date,
    endDate: Date,
    callback: (data: SalesProfitHistory[]) => void
  ): Unsubscribe {
    const sanitizedName = sanitizeCompanyName(companyName);
    const collectionRef = collection(db, `primary_ledger/${sanitizedName}/sales_profit_history`);
    const q = query(
      collectionRef,
      where('asin', '==', asin),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SalesProfitHistory));
      callback(data);
    }, (error) => {
      console.error('Error in sales profit subscription:', error);
      callback([]);
    });
  }

  subscribeToPPCResults(
    companyId: string,
    companyName: string,
    asin: string,
    startDate: Date,
    endDate: Date,
    callback: (data: PPCResults[]) => void
  ): Unsubscribe {
    const sanitizedName = sanitizeCompanyName(companyName);
    const collectionRef = collection(db, `primary_ledger/${sanitizedName}/ppc_results`);
    const q = query(
      collectionRef,
      where('asin', '==', asin),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PPCResults));
      callback(data);
    }, (error) => {
      console.error('Error in PPC results subscription:', error);
      callback([]);
    });
  }

  subscribeToMarginHistory(
    companyId: string,
    companyName: string,
    asin: string,
    startDate: Date,
    endDate: Date,
    callback: (data: MarginHistory[]) => void
  ): Unsubscribe {
    const sanitizedName = sanitizeCompanyName(companyName);
    const collectionRef = collection(db, `primary_ledger/${sanitizedName}/margin_history`);
    const q = query(
      collectionRef,
      where('asin', '==', asin),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'asc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as MarginHistory));
      callback(data);
    }, (error) => {
      console.error('Error in margin history subscription:', error);
      callback([]);
    });
  }
}

export const primaryLedgerService = new PrimaryLedgerService();